import { WrapperGS } from "../WrapperGS";

export interface LayoutGS extends WrapperGS {
    readonly pageContainer: WrapperGS;
    reset(): void | Promise<void>;
}

export interface LayoutConstructorGS<TLayout extends LayoutGS = LayoutGS> {
    new(existingElement?: HTMLElement): TLayout;
    readonly LayoutName: string;
}

/** @internal */
export interface LayoutRegistrationGS {
    readonly constructor: LayoutConstructorGS;
    readonly name: string;
}

/** Application-level layout registry shared by browser and server GUI implementations. */
export class GuiCtrlGS {
    private readonly _layouts = new Map<string, LayoutRegistrationGS>();

    public registerLayout(constructor: LayoutConstructorGS): void {
        const name = (constructor?.LayoutName ?? "").trim();
        if (!name) throw new Error("LayoutName must not be empty.");
        const key = name.toLowerCase();
        if (this._layouts.has(key)) throw new Error(`Layout '${name}' is already registered.`);
        this._layouts.set(key, { constructor, name });
    }

    /** @internal */
    public getRegistration(name: string): LayoutRegistrationGS | undefined {
        return this._layouts.get((name ?? "").toLowerCase());
    }
}

/** Browser GUI controller owns one reusable instance of every registered layout. */
export class GuiBrowserGS extends GuiCtrlGS {
    private readonly _instances = new Map<string, LayoutGS>();
    private _currentLayout?: LayoutGS;
    private _currentPage?: WrapperGS;

    constructor(private readonly _root: HTMLElement) {
        super();
    }

    public getLayout(name: string): LayoutGS {
        const key = (name ?? "").toLowerCase();
        const existing = this._instances.get(key);
        if (existing) return existing;
        const registration = this.getRegistration(key);
        if (!registration) throw new Error(`Layout '${name}' is not registered.`);
        const layout = new registration.constructor();
        GuiBrowserGS.validateLayout(layout, registration.name);
        this._instances.set(key, layout);
        return layout;
    }

    /** @internal */
    public adoptLayout(name: string, element: HTMLElement): LayoutGS {
        const key = (name ?? "").toLowerCase();
        const registration = this.getRegistration(key);
        if (!registration) throw new Error(`Layout '${name}' is not registered.`);
        const layout = new registration.constructor(element);
        if (layout.el !== element) throw new Error(`Layout '${name}' did not adopt the existing DOM root.`);
        GuiBrowserGS.validateLayout(layout, registration.name);
        this._instances.set(key, layout);
        this._currentLayout = layout;
        return layout;
    }

    /** @internal */
    public async place(layoutName: string, page: WrapperGS, preserveExisting: boolean): Promise<LayoutGS> {
        const layout = this.getLayout(layoutName);
        if (preserveExisting) {
            if (layout.el.parentElement === null) this._root.append(layout.el);
            this._currentLayout = layout;
            this._currentPage = page;
            return layout;
        }

        await layout.reset();
        if (this._currentLayout && this._currentLayout !== layout) this._currentLayout.cut();
        if (layout.el.parentElement !== this._root) this._root.append(layout.el);
        if (this._currentPage && this._currentPage !== page) this._currentPage.cut();
        layout.pageContainer.append(page);
        page.show();
        this._currentLayout = layout;
        this._currentPage = page;
        return layout;
    }

    /** @internal */
    public forgetAdoptedLayout(name: string): void {
        const key = (name ?? "").toLowerCase();
        const layout = this._instances.get(key);
        if (layout === this._currentLayout) this._currentLayout = undefined;
        layout?.cut();
        this._instances.delete(key);
        this._currentPage = undefined;
    }

    public get currentLayout(): LayoutGS | undefined { return this._currentLayout; }
    public get currentPage(): WrapperGS | undefined { return this._currentPage; }

    private static validateLayout(layout: LayoutGS, name: string): void {
        if (!layout?.pageContainer) throw new Error(`Layout '${name}' must expose pageContainer.`);
        if (typeof layout.reset !== "function") throw new Error(`Layout '${name}' must implement reset().`);
    }
}
